import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const FinancialForecastingOverview = () => {
  const [selectedQuarter, setSelectedQuarter] = useState('Q1 2025');

  // Mock data for demonstration
  const quarters = ['Q4 2024', 'Q1 2025', 'Q2 2025', 'Q3 2025'];

  // Generate quarter-specific data based on selection
  const generateQuarterData = (quarter) => {
    const quarterDates = {
      'Q4 2024': { start: new Date('2024-10-01'), end: new Date('2024-12-31') },
      'Q1 2025': { start: new Date('2025-01-01'), end: new Date('2025-03-31') },
      'Q2 2025': { start: new Date('2025-04-01'), end: new Date('2025-06-30') },
      'Q3 2025': { start: new Date('2025-07-01'), end: new Date('2025-09-30') },
      'Q4 2025': { start: new Date('2025-10-01'), end: new Date('2025-12-31') }
    };

    const { start: startDate, end: endDate } = quarterDates[quarter];
    const data = [];
    const today = new Date();
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < daysDiff; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];
      const isCurrentDay = currentDate.toDateString() === today.toDateString();
      const isFuture = currentDate > today;

      // Mock cumulative values with quarterly growth patterns
      const quarterMultiplier = quarter.includes('2025') ? 1.15 : 1.0;
      const baseEarned = (i * 2500 + Math.random() * 5000) * quarterMultiplier;
      const baseActual = (i * 2300 + Math.random() * 4800) * quarterMultiplier;

      data.push({
        date: dateStr,
        displayDate: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        earnedLabor: !isFuture ? Math.round(baseEarned) : null,
        actualLabor: !isFuture ? Math.round(baseActual) : null,
        forecastedLabor: isFuture ? Math.round(baseEarned * 1.1) : null,
        scheduledLabor: isFuture ? Math.round(baseActual * 1.05) : null,
        isToday: isCurrentDay,
        isFuture: isFuture
      });
    }
    return data;
  };

  const cumulativeData = generateQuarterData(selectedQuarter);
  const latestData = cumulativeData[cumulativeData.length - 1];
  const currentEarned = latestData.earnedLabor || latestData.forecastedLabor;
  const currentActual = latestData.actualLabor || latestData.scheduledLabor;
  const percentageDiff = ((currentEarned - currentActual) / currentActual * 100);
  const dollarDiff = currentEarned - currentActual;

  // Generate store data based on selected quarter
  const storeData = [
    {
      name: 'Downtown Store',
      data: cumulativeData.map(d => ({
        ...d,
        earnedLabor: d.earnedLabor ? d.earnedLabor * 0.3 : null,
        actualLabor: d.actualLabor ? d.actualLabor * 0.3 : null,
        forecastedLabor: d.forecastedLabor ? d.forecastedLabor * 0.3 : null,
        scheduledLabor: d.scheduledLabor ? d.scheduledLabor * 0.3 : null
      }))
    },
    {
      name: 'Mall Location',
      data: cumulativeData.map(d => ({
        ...d,
        earnedLabor: d.earnedLabor ? d.earnedLabor * 0.25 : null,
        actualLabor: d.actualLabor ? d.actualLabor * 0.25 : null,
        forecastedLabor: d.forecastedLabor ? d.forecastedLabor * 0.25 : null,
        scheduledLabor: d.scheduledLabor ? d.scheduledLabor * 0.25 : null
      }))
    },
    {
      name: 'Airport Terminal',
      data: cumulativeData.map(d => ({
        ...d,
        earnedLabor: d.earnedLabor ? d.earnedLabor * 0.2 : null,
        actualLabor: d.actualLabor ? d.actualLabor * 0.2 : null,
        forecastedLabor: d.forecastedLabor ? d.forecastedLabor * 0.2 : null,
        scheduledLabor: d.scheduledLabor ? d.scheduledLabor * 0.2 : null
      }))
    },
    {
      name: 'Suburban Plaza',
      data: cumulativeData.map(d => ({
        ...d,
        earnedLabor: d.earnedLabor ? d.earnedLabor * 0.25 : null,
        actualLabor: d.actualLabor ? d.actualLabor * 0.25 : null,
        forecastedLabor: d.forecastedLabor ? d.forecastedLabor * 0.25 : null,
        scheduledLabor: d.scheduledLabor ? d.scheduledLabor * 0.25 : null
      }))
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-800">{data.displayDate}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: ${entry.value?.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = (data, title = null, showTitle = false) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {showTitle && title && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <span className="text-sm text-gray-600">{storeData.length} locations</span>
        </div>
      )}
      {!showTitle && title && (
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-medium text-gray-700">{title}</h4>
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          {/* <Legend /> */}
          <Line
            type="monotone"
            dataKey="earnedLabor"
            stroke="#10b981"
            strokeWidth={2}
            name="Earned Labor"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="actualLabor"
            stroke="#7c3aed"
            strokeWidth={2}
            name="Actual Labor"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="forecastedLabor"
            stroke="#10b981"
            strokeWidth={2}
            strokeDasharray="5 5"
            name="Forecasted Labor"
            dot={false}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="scheduledLabor"
            stroke="#7c3aed"
            strokeWidth={2}
            strokeDasharray="5 5"
            name="Scheduled Labor"
            dot={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen rounded-xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Financial Forecasting</h1>
        <p className="text-gray-600">Labor performance tracking and forecasting across all locations</p>
      </div>

      {/* Chart Legend */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Chart Legend</h3>
        <div className="grid md:grid-cols-1 grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <div className="flex items-center mb-2">
              <div className="w-4 h-0.5 bg-green-500 mr-2"></div>
              <span><strong>Earned Labor (Solid):</strong> Actual labor earned for past dates</span>
            </div>
            <div className="flex items-center mb-2">
              <div className="w-4 h-0.5 bg-violet-600 mr-2"></div>
              <span><strong>Actual Labor (Solid):</strong> Actual labor costs for past dates</span>
            </div>
          </div>
          <div>
            <div className="flex items-center mb-2">
              <div className="w-4 h-0.5 bg-green-500 mr-2" style={{borderTop: '2px dashed #10b981', borderBottom: 'none', backgroundColor: 'transparent'}}></div>
              <span><strong>Forecasted Labor (Dashed):</strong> Projected labor earnings for future dates</span>
            </div>
            <div className="flex items-center mb-2">
              <div className="w-4 h-0.5 bg-violet-600 mr-2" style={{borderTop: '2px dashed #7c3aed', borderBottom: 'none', backgroundColor: 'transparent'}}></div>
              <span><strong>Scheduled Labor (Dashed):</strong> Planned labor costs for future dates</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid md:grid-cols-1 grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">Performance vs Target</div>
          <div className={`text-2xl font-bold ${percentageDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {percentageDiff >= 0 ? '+' : ''}{percentageDiff.toFixed(1)}%
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">Dollar Difference</div>
          <div className={`text-2xl font-bold ${dollarDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {dollarDiff >= 0 ? '+' : ''}${dollarDiff.toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">Locations</div>
          <div className="text-2xl font-bold text-gray-900">{storeData.length}</div>
        </div>
      </div>

      {/* Main Chart */}
      <div className="mb-6">
        {renderChart(cumulativeData, "Cumulative Labor Performance", true)}

        {/* Quarter Selection */}
        <div className="flex justify-center mt-4 space-x-2">
          {quarters.map(quarter => (
            <button
              key={quarter}
              onClick={() => setSelectedQuarter(quarter)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedQuarter === quarter
                  ? 'bg-violet-700 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {quarter}
            </button>
          ))}
        </div>
      </div>

      {/* Individual Store Charts */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Individual Location Performance</h2>
        <div className="grid lg:grid-cols-1 grid-cols-2 gap-6">
          {storeData.map((store, index) => (
            <div key={index}>
              {renderChart(store.data, store.name)}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default FinancialForecastingOverview;
